import React, { useState } from 'react';
import { Form, Input, Button, Alert, Checkbox, Progress } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useAuthStore } from '../../stores/authStore';
import { TermsModal } from './TermsModal';
import { validatePassword, checkPasswordStrength } from '../../utils/passwordValidator';
import { logger } from '../../utils/logger';
import { TestVersionNotice } from '../common/TestVersionNotice';

import './AuthStyles.css';

interface RegisterFormProps {
  onSuccess?: () => void;
  onSwitchToLogin?: () => void;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({ onSuccess, onSwitchToLogin }) => {
  const [form] = Form.useForm();
  const { register, isLoading, error, clearError } = useAuthStore();
  const [successMessage, setSuccessMessage] = React.useState<string>('');
  const [termsModalVisible, setTermsModalVisible] = useState(false);
  const [privacyModalVisible, setPrivacyModalVisible] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<{ score: number; level: string; feedback: string[] } | null>(null);



  const handleSubmit = async (values: {
    username: string;
    email: string;
    password: string;
    phoneCountryCode?: number;
    phoneNumber?: string;
    organization?: string;
    callSign?: string;
  }) => {
    clearError();
    setSuccessMessage(''); // 清除之前的成功消息
    
    try {
      logger.debug('开始注册，提交数据');
      await register(values);
      logger.debug('注册成功，显示成功消息');
      
      // 设置成功消息，使用Alert组件显示
      setSuccessMessage('注册成功！欢迎加入国赫 WaveLink');
      
      // 延迟执行onSuccess回调
      setTimeout(() => {
        logger.debug('执行onSuccess回调');
        onSuccess?.();
      }, 2000); // 给用户2秒时间看到成功消息
      
    } catch (error) {
      logger.error('注册失败:', error);
      // 错误已经在store中处理，这里不需要额外处理
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <TestVersionNotice variant="auth" />
        {/* Logo Section */}
        <div className="logo-section">
          <div className="hexagon-logo">
            <img src="/images/GH-logo.png" alt="GH Logo" />
          </div>
          <h1 className="brand-text">WaveLink</h1>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="success-message">
            <Alert
              message="注册成功"
              description={successMessage}
              type="success"
              showIcon
              closable
              onClose={() => setSuccessMessage('')}
            />
          </div>
        )}

        {/* Error Messages */}
        {error && (
          <div className="error-message">
            <Alert
              message="注册失败"
              description={error}
              type="error"
              showIcon
              closable
            />
          </div>
        )}

        {/* Register Form */}
        <Form
          form={form}
          onFinish={handleSubmit}
          layout="vertical"
          className="auth-form"
          initialValues={{ agreedToTerms: false }}
        >
          {/* 必填字段 */}
          <Form.Item
            name="username"
            rules={[
              { required: true, message: '请输入用户名' },
              { min: 3, max: 32, message: '用户名长度必须在3-32个字符之间' },
              { pattern: /^[a-zA-Z0-9_\u4e00-\u9fa5]+$/, message: '用户名只能包含字母、数字、下划线和中文' }
            ]}
            className="form-item"
          >
            <Input
              prefix={<UserOutlined className="input-icon" />}
              placeholder="用户名"
              className="auth-input"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 8, max: 128, message: '密码长度必须在8-128个字符之间' },
              {
                validator: (_, value) => {
                  if (!value) {
                    return Promise.resolve();
                  }
                  const validation = validatePassword(value);
                  if (validation.valid) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(validation.message));
                },
              },
            ]}
            className="form-item"
          >
            <Input.Password
              prefix={<LockOutlined className="input-icon" />}
              placeholder="密码（至少8位，包含大小写字母、数字和特殊字符）"
              className="auth-input"
              size="large"
              onChange={(e) => {
                const password = e.target.value;
                if (password) {
                  const strength = checkPasswordStrength(password);
                  setPasswordStrength(strength);
                } else {
                  setPasswordStrength(null);
                }
              }}
            />
          </Form.Item>
          
          {/* 密码强度指示器 */}
          {passwordStrength && (
            <div style={{ marginTop: -16, marginBottom: 16 }}>
              <Progress
                percent={(passwordStrength.score / 5) * 100}
                status={passwordStrength.level === 'weak' ? 'exception' : passwordStrength.level === 'strong' ? 'success' : 'active'}
                strokeColor={
                  passwordStrength.level === 'weak' ? '#ff4d4f' :
                  passwordStrength.level === 'fair' ? '#faad14' :
                  passwordStrength.level === 'good' ? '#1890ff' :
                  '#52c41a'
                }
                showInfo={false}
              />
              <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                密码强度: {
                  passwordStrength.level === 'weak' ? '弱' :
                  passwordStrength.level === 'fair' ? '一般' :
                  passwordStrength.level === 'good' ? '良好' :
                  '强'
                }
              </div>
            </div>
          )}

          <Form.Item
            name="agreedToTerms"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value ? Promise.resolve() : Promise.reject(new Error('请先阅读并同意用户协议和隐私政策')),
              },
            ]}
            className="form-item terms-checkbox-item"
          >
            <Checkbox>
              我已阅读并同意
              <button 
                type="button" 
                className="terms-link-inline"
                onClick={(e) => {
                  e.preventDefault();
                  setTermsModalVisible(true);
                }}
              >
                用户协议
              </button>
              和
              <button 
                type="button" 
                className="terms-link-inline"
                onClick={(e) => {
                  e.preventDefault();
                  setPrivacyModalVisible(true);
                }}
              >
                隐私政策
              </button>
            </Checkbox>
          </Form.Item>

          <Form.Item className="form-item">
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
              className="auth-button"
              size="large"
            >
              {isLoading ? '注册中...' : '注册'}
            </Button>
          </Form.Item>
        </Form>

        {/* Login Link */}
        <div className="auth-links">
          <span className="link-text">已有账户？</span>
          <button 
            type="button" 
            className="link-button"
            onClick={onSwitchToLogin}
          >
            立即登录
          </button>
        </div>

      </div>
      
      <TermsModal
        visible={termsModalVisible}
        type="terms"
        onClose={() => setTermsModalVisible(false)}
      />
      <TermsModal
        visible={privacyModalVisible}
        type="privacy"
        onClose={() => setPrivacyModalVisible(false)}
      />
    </div>
  );
};

export default RegisterForm; 